<template>
  <div class="container-fluid vacation-calendar">
    <!-- Success and Error Messages -->
    <div
      v-if="successMessage"
      class="alert alert-success alert-dismissible fade show fixed-alert"
      role="alert"
    >
      {{ successMessage }}
      <button
        type="button"
        class="btn-close"
        @click="successMessage = ''"
      ></button>
    </div>
    <div
      v-if="errorMessage"
      class="alert alert-danger alert-dismissible fade show fixed-alert"
      role="alert"
    >
      {{ errorMessage }}
      <button
        type="button"
        class="btn-close"
        @click="errorMessage = ''"
      ></button>
    </div>

    <!-- Closed Year Warning -->
    <div v-if="isYearClosed && !isActingAsAdmin" class="alert alert-warning mt-3">
      {{ $t("year_closed_warning") }}
    </div>

    <div>
      <i class="help-icon fas fa-question-circle" @click="showHelp = true"></i>
    </div>
    <div class="d-flex main-content">
      <!-- Left Section with Tabs -->
      <div class="left-section">
        <div class="tab-container">
          <div
            class="tab reasons-tab"
            :class="{ active: activeSection === 'reasons' }"
            @click="setActiveSection('reasons')"
          >
            {{ $t("reasons") }}
          </div>
          <div
            class="tab statistics-tab"
            :class="{ active: activeSection === 'statistics' }"
            @click="setActiveSection('statistics')"
          >
            {{ $t("statistics") }}
          </div>
        </div>

        <!-- Reasons Section -->
        <ul v-if="activeSection === 'reasons'" class="list-group reasons-list">
          <li
            v-for="type in absenceTypes"
            :key="type.id"
            class="list-group-item d-flex justify-content-between align-items-center reason-item"
            :class="{ selected: selectedAbsenceType?.id === type.id }"
            @click="toggleAbsenceTypeSelection(type)"
          >
            <span>
              <span
                :style="{ backgroundColor: type.color_code }"
                class="color-box me-2"
              ></span>
              {{ type.name }}
            </span>
            <span class="badge" :style="{ backgroundColor: type.color_code }">
              <div class="square-spacer"></div>
            </span>
          </li>
        </ul>

        <!-- Statistics Section -->
        <div v-if="activeSection === 'statistics'" class="statistics-list">
          <p>{{ $t("transferDeadline") }}: {{ formatDate(statistics.transferDeadline) }}</p>
          <table class="table table-bordered">
            <tbody>
              <tr>
                <td colspan="2">
                  <strong>{{ $t("vacationStatistics") }}</strong>
                </td>
              </tr>
              <tr>
                <td>{{ $t("quota") }}</td>
                <td>{{ statistics.vacationQuota }}</td>
              </tr>
              <tr>
                <td>{{ $t("currentlyAvailable") }}</td>
                <td>{{ statistics.vacationCurrentDate }}</td>
              </tr>
              <tr>
                <td>{{ $t("usedDays") }}</td>
                <td>{{ statistics.vacationUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t("availableDays") }}</td>
                <td>{{ statistics.vacationAvailable }}</td>
              </tr>
              <tr>
                <td colspan="2" class="fw-bold border-top">
                  {{ $t("sickLeaveStatistics") }}
                </td>
              </tr>
              <tr>
                <td>{{ $t("quota") }}</td>
                <td>{{ statistics.sickLeaveQuota }}</td>
              </tr>
              <tr>
                <td>{{ $t("usedDays") }}</td>
                <td>{{ statistics.sickLeaveUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t("availableDays") }}</td>
                <td>{{ statistics.sickLeaveAvailable }}</td>
              </tr>
              <tr>
                <td colspan="2" class="fw-bold border-top">
                  {{ $t("otherAbsences") }}
                </td>
              </tr>
              <tr>
                <td>{{ $t("recoveryDays") }}</td>
                <td>{{ statistics.recoveryDays }}</td>
              </tr>
              <tr>
                <td>{{ $t("unpaidAbsence") }}</td>
                <td>{{ statistics.unpaidAbsences }}</td>
              </tr>
              <tr>
                <td colspan="2">
                  <strong>{{ $t("transferredDayStatistics") }}</strong>
                </td>
              </tr>
              <tr>
                <td>{{ $t("quota") }}</td>
                <td>{{ statistics.transferredDays }}</td>
              </tr>
              <tr>
                <td>{{ $t("usedDays") }}</td>
                <td>{{ statistics.transferredUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t("availableDays") }}</td>
                <td>{{ statistics.transferredDaysLeft }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Viewing User Status (Admin only) -->
        <div v-if="store.viewingUser" class="viewing-user-card mt-3 p-3">
          <div class="viewing-label">
            <i class="bi bi-person-badge me-2"></i>
            {{
              store.appLanguage === "uk"
                ? "Перегляд календаря:"
                : "Viewing calendar:"
            }}
          </div>
          <div class="viewing-name">
            {{ store.viewingUser.first_name }} {{ store.viewingUser.last_name }}
          </div>
        </div>
      </div>

      <!-- Calendar Section -->
      <div class="right-section">
        <YearlyCalendar
          ref="calendar"
          :absences="absences"
          :holidays="holidays"
          :available-years="availableYears"
          :current-year-index="currentYearIndex"
          :selected-absence-type="selectedAbsenceType"
          :restricted-dates="restrictedDates"
          :enable-two-week-rule="
            availableYears[currentYearIndex]?.enable_two_week_rule
          "
          :is-read-only="isYearClosed && !isActingAsAdmin"
          v-model:selected-days="calendarSelections"
          @change-year="changeYear"
        />

        <!-- Only show Save if user selected new days (not existing approved absences) -->
        <div
          v-if="newlySelectedDates.length && !selectedApprovedAbsences.length"
          class="d-flex justify-content-end mt-3"
        >
          <button class="btn btn-secondary me-2" @click="cancelSelection">
            {{ $t("cancel") }}
          </button>
          <button class="btn btn-primary" @click="saveChanges" :disabled="isYearClosed && !isActingAsAdmin">
            {{ $t("save") }}
          </button>
        </div>

        <!-- Actions for Pending or Approved Absences -->
        <div
          v-if="selectedPendingAbsences.length || hasDeletableApprovedAbsences || selectedApprovedAbsences.length"
          class="d-flex justify-content-end mt-3"
        >
          <button class="btn btn-secondary me-2" @click="cancelSelection">
            {{ $t("cancel") }}
          </button>
          <button class="btn btn-danger" @click="openConfirmDeleteModal" :disabled="isYearClosed && !isActingAsAdmin">
            {{ $t("delete") }}
          </button>
        </div>

        <!-- Confirm Delete Modal -->
        <div
          class="modal fade"
          id="confirmDeleteModal"
          tabindex="-1"
          aria-labelledby="confirmDeleteModalLabel"
          aria-hidden="true"
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="confirmDeleteModalLabel">
                  {{ $t("confirm_delete") }}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div class="modal-body">
                {{ $t("confirmDeleteUserAbsence") }}
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  {{ $t("cancel") }}
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="deleteSelectedAbsences"
                >
                  {{ $t("delete") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showHelp" class="help-modal">
          <div class="help-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t("calendarHelpTitle") }}</h5>
              <button
                type="button"
                class="btn-close"
                @click="showHelp = false"
                aria-label="Close"
              ></button>
            </div>

            <div class="modal-body">
              <ol>
                <li>{{ $t("calendarHelpStep1") }}</li>
                <li>{{ $t("calendarHelpStep2") }}</li>
                <li>{{ $t("calendarHelpStep3") }}</li>
                <li>{{ $t("calendarHelpStep4") }}</li>
                <li>{{ $t("calendarHelpStep5") }}</li>
              </ol>
            </div>
            <div
              class="modal-footer"
              :style="
                isUserAdmin && appVersion
                  ? { justifyContent: 'space-between' }
                  : { justifyContent: 'flex-end' }
              "
            >
              <div v-if="isUserAdmin && appVersion" class="version-label">
                ver: {{ appVersion }}
              </div>
              <button class="btn btn-success" @click="showHelp = false">
                {{ $t("close") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMainStore } from "@/store";
import axios from "axios";
import YearlyCalendar from "@/views/vacation/YearlyCalendar.vue";

export default {
  name: "VacationCalendar",
  components: { YearlyCalendar },

  data() {
    return {
      activeSection: "reasons",
      selectedAbsenceType: null,
      newlySelectedDates: [],
      selectedPendingAbsences: [],
      selectedApprovedAbsences: [],
      selectedDeletableApprovedAbsences: [],
      calendarSelections: [],
      absences: [],
      absenceTypes: [],
      restrictedDates: [],
      holidays: [],
      absenceStatistics: [],
      availableYears: [{ id: null, year: new Date().getFullYear() }],
      currentYearIndex: 0,
      successMessage: "",
      errorMessage: "",
      statistics: {
        vacationQuota: 20,
        vacationCurrentDate: 0,
        vacationUsed: 0,
        vacationAvailable: 0,
        sickLeaveQuota: 10,
        sickLeaveUsed: 0,
        sickLeaveAvailable: 10,
        recoveryDays: 0,
        unpaidAbsences: 0,
        transferredDays: 0,
        transferredUsed: 0,
        transferredDaysLeft: 0,
        transferDeadline: null,
      },
      showHelp: false,
      appVersion:
        process.env.VUE_APP_FRONTEND_VERSION ||
        "mindydockerhub/vacation-new:main-frontend-6d0c8ee9",
    };
  },
  mounted() {
    this.checkConfirmationStatus();

    this.fetchYearSettings();
    this.fetchAbsenceTypes();
    this.fetchAbsences();
    this.fetchStatistics();
    this.fetchRestrictedDates();
  },
  computed: {
    store: () => useMainStore(),
    canManageAllAbsences() {
      return this.store.user.permissions.includes("users.manage_all_absences");
    },
    isActingAsAdmin() {
      return this.canManageAllAbsences && !!this.$route.query.user_ids;
    },
    todayISO() {
      // local date as YYYY-MM-DD; string compare works for ISO format
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    hasDeletableApprovedAbsences() {
      const today = new Date().toISOString().slice(0, 10);
      const currentUserId = Number(
        this.store.user.id || localStorage.getItem("userID"),
      );

      return this.selectedApprovedAbsences.some(({ id }) => {
        const absence = this.absences.find((a) => a.id === id);
        if (!absence) return false;

        // Admins can delete any approved only when acting as admin
        if (this.isActingAsAdmin) return true;

        // Normal user: only own + future
        const isOwn =
          typeof absence.user === "object"
            ? Number(absence.user.id) === currentUserId
            : Number(absence.user) === currentUserId;

        return isOwn && absence.absence_date > today;
      });
    },
    isUserAdmin() {
      return this.store.user.permissions.includes("users.manage_all_absences");
    },
    isYearClosed() {
      const currentYear = this.availableYears[this.currentYearIndex];
      return currentYear ? currentYear.is_closed : false;
    },
  },
  watch: {
    calendarSelections(newVal) {
      const actingAsAdmin = this.isActingAsAdmin;

      this.newlySelectedDates = newVal.filter((date) => {
        const existingAbsence = this.absences.find(
          (a) => a.absence_date === date,
        );
        return (
          !existingAbsence ||
          (existingAbsence.status === "approved" && actingAsAdmin)
        );
      });

      this.selectedPendingAbsences = newVal
        .map((date) => {
          const absence = this.absences.find(
            (a) => a.absence_date === date && a.status === "pending",
          );
          return absence ? { id: absence.id, date, absence } : null;
        })
        .filter(Boolean);

      this.selectedApprovedAbsences = newVal
        .map((date) => {
          const absence = this.absences.find(
            (a) => a.absence_date === date && a.status === "approved",
          );
          return absence ? { id: absence.id, date, absence } : null;
        })
        .filter(Boolean);

      // NEW: which approved absences are deletable
      this.selectedDeletableApprovedAbsences =
        this.selectedApprovedAbsences.filter((item) => {
          const a = item.absence;

          // Admin-like can delete any approved
          if (actingAsAdmin) return true;

          // Normal user: only own approved absences AND only future dates
          // Your API list might return `user` (object) or `user_id` — support both.
          const absenceUserId = Number(a?.user?.id || a?.user_id);
          const isOwn = absenceUserId === Number(this.currentUserId);
          const isFuture = item.date > this.todayISO; // strictly after today
          return isOwn && isFuture;
        });
    },
    $route: {
      handler() {
        this.fetchAbsences();
        this.fetchTargetUser();
      },
      immediate: true,
    },
  },
  unmounted() {
    this.store.viewingUser = null;
  },
  methods: {
    reloadCalendar() {
      this.fetchAbsences();
      this.fetchStatistics();
      this.newlySelectedDates = [];
      this.selectedPendingAbsences = [];
      this.calendarSelections = [];
      this.selectedAbsenceType = null;
      this.selectedApprovedAbsences = [];
      this.selectedDeletableApprovedAbsences = [];
    },
    async fetchStatistics() {
      try {
        const userIds = this.$route.query.user_ids;

        const params = {};
        if (userIds) {
          params.user_ids = userIds;
        }

        const response = await axios.get(
          `/api/v1/vacation/absences/statistics/`,
          {
            params,
          },
        );

        this.statistics = response.data;
      } catch (error) {
        this.errorMessage = "Failed to fetch statistics.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    async fetchRestrictedDates() {
      try {
        const response = await axios.get(
          "/api/v1/vacation/absences/restricted-dates/",
        );
        this.restrictedDates = response.data;
      } catch (error) {
        console.error("Failed to fetch restricted dates", error);
      }
    },
    openConfirmDeleteModal() {
      const modal = new bootstrap.Modal(
        document.getElementById("confirmDeleteModal"),
      );
      modal.show();
    },
    closeConfirmDeleteModal() {
      const modalElement = document.getElementById("confirmDeleteModal");
      if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
    },
    getDeletableApprovedIds() {
      const today = new Date().toISOString().slice(0, 10);
      const currentUserId = Number(
        this.store.user.id || localStorage.getItem("userID"),
      );

      return this.selectedApprovedAbsences
        .map(({ id }) => this.absences.find((a) => a.id === id))
        .filter(Boolean)
        .filter((absence) => {
          if (this.isActingAsAdmin) return true;

          const isOwn =
            typeof absence.user === "object"
              ? Number(absence.user.id) === currentUserId
              : Number(absence.user) === currentUserId;

          // const isFuture = absence.absence_date > today;
          const isFuture = true
          return isOwn && isFuture;
        })
        .map((a) => a.id);
    },
    async deleteSelectedAbsences() {
      try {
        const pendingIds = this.selectedPendingAbsences.map((item) => item.id);
        const approvedIds = this.getDeletableApprovedIds();

        const absenceIds = [...new Set([...pendingIds, ...approvedIds])];

        if (!absenceIds.length) {
          this.errorMessage =
            "Nothing to delete: only future approved absences can be deleted.";
          setTimeout(() => (this.errorMessage = ""), 3000);
          return;
        }

        // Debug (optional): verify requests are really sent
        console.log("Deleting absences:", absenceIds);

        await Promise.all(
          absenceIds.map((id) =>
            axios.delete(`/api/v1/vacation/absences/${id}/`),
          ),
        );

        this.successMessage = "Selected absences deleted successfully.";
        this.closeConfirmDeleteModal();

        await this.reloadCalendar();

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        this.errorMessage =
          this.getErrorMessage(error) || "Failed to delete selected absences.";
        setTimeout(() => (this.errorMessage = ""), 5000);
      }
    },
    async fetchYearSettings() {
      try {
        const response = await axios.get(`/api/v1/vacation/year-settings/`);
        this.availableYears = response.data.results;
        this.availableYears.sort((a, b) => a.year - b.year);
        this.currentYearIndex = this.availableYears.findIndex(
          (yearSetting) => yearSetting.year === new Date().getFullYear(),
        );
        if (this.currentYearIndex === -1) {
          this.currentYearIndex = 0;
        }
        const yearId = this.availableYears[this.currentYearIndex]?.id;
        if (yearId) {
          await this.fetchHolidays(yearId);
          await this.fetchAbsences();
          await this.fetchStatistics();
        }
      } catch (error) {
        this.errorMessage = "Failed to fetch year settings.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    changeYear(newYearIndex) {
      this.currentYearIndex = newYearIndex;
      const yearId = this.availableYears[newYearIndex]?.id;
      if (yearId) {
        this.fetchHolidays(yearId);
        this.fetchAbsences();
        if (this.activeSection === "statistics") {
          this.fetchStatistics();
        }
      }
    },
    async fetchHolidays(yearId) {
      try {
        const response = await axios.get(
          `/api/v1/vacation/holidays?year_id=${yearId}`,
        );
        this.holidays = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = `Failed to fetch holidays for YearSetting ID ${yearId}.`;
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    async fetchAbsences() {
      try {
        const userId = this.$route.query.user_ids;
        const yearId = this.availableYears[this.currentYearIndex]?.id;
        if (!yearId) return;

        const response = await axios.get(`/api/v1/vacation/absences/`, {
          params: {
            year_id: yearId,
            user_ids: userId,
          },
        });

        const raw = response.data.results || response.data;

        // ❗ Hide declined absences from the calendar completely
        this.absences = raw.filter((a) => a.status !== "declined");
      } catch (error) {
        this.errorMessage = "Failed to fetch absences.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    async fetchTargetUser() {
      const targetId = this.$route.query.user_ids;
      if (this.isActingAsAdmin && targetId) {
        try {
          const response = await axios.get(`/api/v1/admin/users/${targetId}/`);
          this.store.viewingUser = response.data;
        } catch (error) {
          console.error("Failed to fetch target user", error);
          this.store.viewingUser = null;
        }
      } else {
        this.store.viewingUser = null;
      }
    },
    setActiveSection(section) {
      this.activeSection = section;
      if (section === "statistics") {
        this.fetchStatistics(); // Ensure statistics are fetched when the section is activated
      }
    },
    toggleAbsenceTypeSelection(type) {
      // If the same type is clicked again → deselect
      if (this.selectedAbsenceType?.id === type.id) {
        this.selectedAbsenceType = null;
        this.newlySelectedDates = [];
        this.selectedPendingAbsences = [];
        return;
      }

      // Else → switch to new type and clear selection
      this.selectedAbsenceType = type;
      this.newlySelectedDates = [];
      this.selectedPendingAbsences = [];
    },
    async fetchAbsenceTypes() {
      try {
        const response = await axios.get("/api/v1/vacation/absence-types/");
        this.absenceTypes = response.data.results;
      } catch (error) {
        this.errorMessage = "Failed to fetch absence types.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },
    cancelSelection() {
      this.newlySelectedDates = [];
      this.selectedPendingAbsences = [];
      this.calendarSelections = [];
      this.selectedApprovedAbsences = [];
      this.selectedDeletableApprovedAbsences = [];
      this.reloadCalendar();
    },
    async saveChanges() {
      if (!this.selectedAbsenceType) {
        this.errorMessage = "Please select an absence type before saving.";
        setTimeout(() => (this.errorMessage = ""), 3000);
        return;
      }
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const targetUserId =
          urlParams.get("user_ids") || localStorage.getItem("userID");
        const yearId = this.availableYears[this.currentYearIndex].id;

        if (!targetUserId || !yearId) {
          this.errorMessage = "User or year information is missing.";
          return;
        }

        const status = this.isActingAsAdmin ? "approved" : "pending";

        const requests = this.newlySelectedDates.map((date) => ({
          user: targetUserId,
          year_settings: yearId,
          absence_type: this.selectedAbsenceType.id,
          absence_date: date,
          status: status,
        }));

        await axios.post("/api/v1/vacation/absences/", requests);
        this.successMessage = "Absence request saved successfully.";
        setTimeout(() => {
          this.successMessage = "";
          this.reloadCalendar();
        }, 500);
        await this.fetchAbsences();
        this.newlySelectedDates = [];
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        setTimeout(() => (this.errorMessage = ""), 5000);
      }
    },
    getErrorMessage(error) {
      if (!error?.response?.data) {
        return "An unexpected error occurred. Please try again.";
      }

      const data = error.response.data;

      // Bulk-create case: backend returns list[dict]
      if (Array.isArray(data)) {
        // Find first item that has any meaningful errors
        const firstWithErrors = data.find(
          (item) =>
            item &&
            typeof item === "object" &&
            (item.non_field_errors?.length ||
              item.detail ||
              Object.keys(item).some(
                (k) => Array.isArray(item[k]) && item[k].length,
              )),
        );

        if (!firstWithErrors) {
          return "An error occurred.";
        }

        // Prefer non_field_errors if present
        if (
          Array.isArray(firstWithErrors.non_field_errors) &&
          firstWithErrors.non_field_errors.length
        ) {
          return String(firstWithErrors.non_field_errors[0]);
        }

        // If DRF-style detail exists
        if (firstWithErrors.detail) {
          return String(firstWithErrors.detail);
        }

        // Otherwise take the first error field and first message from it
        const firstKey = Object.keys(firstWithErrors).find(
          (k) => Array.isArray(firstWithErrors[k]) && firstWithErrors[k].length,
        );
        if (firstKey) {
          return String(firstWithErrors[firstKey][0]);
        }

        return "An error occurred.";
      }

      // Single object error responses
      if (typeof data === "string") return data;
      if (data.detail) return String(data.detail);

      if (
        Array.isArray(data.non_field_errors) &&
        data.non_field_errors.length
      ) {
        return String(data.non_field_errors[0]);
      }

      // Generic dict-of-lists fallback (take first message only)
      if (typeof data === "object") {
        const firstKey = Object.keys(data).find(
          (k) => Array.isArray(data[k]) && data[k].length,
        );
        if (firstKey) return String(data[firstKey][0]);
      }

      return "An error occurred.";
    },
    checkConfirmationStatus() {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get("confirm_status");

      if (status === "success") {
        this.successMessage = this.$t("vacation_confirmed_success");
        this.cleanUrlFromStatus();
      } else if (status === "error") {
        this.errorMessage = this.$t("vacation_confirmed_error");
        this.cleanUrlFromStatus();
      }
    },
    cleanUrlFromStatus() {
      const url = new URL(window.location.href);
      url.searchParams.delete("confirm_status");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search,
      );

      setTimeout(() => {
        this.successMessage = "";
        this.errorMessage = "";
      }, 7000);
    },
    formatDate(date) {
      if (!date) return "";
      const [year, month, day] = date.split("-");
      return `${day}.${month}.${year}`;
    },
  },
};
</script>

<style scoped>
.help-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 40vw;
  width: 80%;
  min-width: 300px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  text-align: left;
  line-height: 1.5;
  word-wrap: break-word;
}

.square-spacer {
  height: 15px;
  width: 10px;
  display: block;
}

.viewing-user-card {
  background-color: var(--color-card-bg);
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.viewing-label {
  font-size: 0.85rem;
  color: var(--color-muted);
  margin-bottom: 4px;
}

.viewing-name {
  font-weight: bold;
  color: var(--color-primary);
  font-size: 1.1rem;
}
</style>
